import { useParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Menu, CheckCircle } from "lucide-react";
import ChatWidget from '../../components/ChatWidget';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import removeMd from 'remove-markdown';


export default function ElearningPageResponsive() {
  const { subjectId } = useParams();
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [components, setComponents] = useState([]);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [showModules, setShowModules] = useState(false);
  const [subjectName, setSubjectName] = useState("");
  const [completed, setCompleted] = useState([]);
  const [contentHTML, setContentHTML] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('theme') === 'dark');
  const contentRef = useRef();

  const [hasAccess, setHasAccess] = useState(false);
  const [subjectPrice, setSubjectPrice] = useState(99);

  const user = JSON.parse(localStorage.getItem("user"));

  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [highlightedSentenceIdx, setHighlightedSentenceIdx] = useState(-1);
  const [spokenCharIndex, setSpokenCharIndex] = useState(-1);
  const synthRef = useRef(window.speechSynthesis);
  const utteranceRef = useRef(null);
  

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDarkMode);
    localStorage.setItem("theme", isDarkMode ? "dark" : "light");
  }, [isDarkMode]);

  useEffect(() => {
    if (subjectId) {
      axios.get(`https://backend.scholarkart.in/api/modules?subjectId=${subjectId}`).then((res) => setModules(res.data));
      axios.get(`https://backend.scholarkart.in/api/subjects/${subjectId}`)
      .then((res) => {
        setSubjectName(res.data.subjectName);
        setSubjectPrice(res.data.price); // 💰
      });
      if (user?._id) {
        axios.get(`https://backend.scholarkart.in/api/users/${user._id}/completed-submodules`).then(res => setCompleted(res.data));
      }
    }
  }, [subjectId]);

  // Check if user has access to the subject
  useEffect(() => {
    const checkAccess = async () => {
      if (subjectId && user?._id) {
        if (user.collegeId) {
          try {
            const res = await axios.get(`https://backend.scholarkart.in/api/college-course-access/${user.collegeId}/${subjectId}`);
            if (res.data.access) {
              setHasAccess(true);
              return;
            }
          } catch (err) {
            console.error("Institutional access check failed:", err);
          }
        }
  
        // Fallback to personal purchase check
        const res = await axios.get(`https://backend.scholarkart.in/api/user/${user._id}/premium-subjects`);
        setHasAccess(res.data.subjectIds.includes(subjectId));
      }
    };
  
    checkAccess();
  }, [subjectId, user?._id]);
  



const speakText = () => {
  if (!contentHTML || isSpeaking) return;

  const text = contentRef.current?.innerText;
  if (!text) return;

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = "en-IN";
  utterance.rate = 0.85;

  utterance.onboundary = (event) => {
    if (event.name === "word") {
      setSpokenCharIndex(event.charIndex); // track current word start
    }
  };

  utterance.onend = () => {
    setIsSpeaking(false);
    setIsPaused(false);
    setSpokenCharIndex(-1); // reset
  };

  synthRef.current.speak(utterance);
  utteranceRef.current = utterance;
  setIsSpeaking(true);
  setIsPaused(false);
};


const speakText1 = (text) => {
  if (!text || isSpeaking) return;

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = "en-IN";
  utterance.rate = 0.9;

  utterance.onboundary = (event) => {
    if (event.name === 'word') {
      setSpokenCharIndex(event.charIndex);
    }
  };

  utterance.onend = () => {
    setIsSpeaking(false);
    setIsPaused(false);
    setSpokenCharIndex(-1);
  };

  synthRef.current.speak(utterance);
  utteranceRef.current = utterance;
  setIsSpeaking(true);
};

const pauseSpeaking = () => {
  if (synthRef.current.speaking && !synthRef.current.paused) {
    synthRef.current.pause();
    setIsPaused(true);
  }
};

const resumeSpeaking = () => {
  if (synthRef.current.paused) {
    synthRef.current.resume();
    setIsPaused(false);
  }
};

const stopSpeaking = () => {
  if (synthRef.current.speaking || synthRef.current.paused) {
    synthRef.current.cancel();
    setIsSpeaking(false);
    setIsPaused(false);
  }
};

const SpeakControls = ({ text }) =>
  text ? (
    <div className="flex gap-2 mb-3">
      <button
        onClick={() => speakText1(text)}
        disabled={isSpeaking}
        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
      >
        🔊 Read Aloud
      </button>
      {isSpeaking && (
        <>
          <button
            onClick={isPaused ? resumeSpeaking : pauseSpeaking}
            className="px-3 py-1 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600"
          >
            {isPaused ? "▶️ Resume" : "⏸ Pause"}
          </button>
          <button
            onClick={stopSpeaking}
            className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
          >
            ⏹ Stop
          </button>
        </>
      )}
    </div>
  ) : null;





  const handleModuleClick = async (module) => {
    setSelectedModule(module);
    const res = await axios.get(`https://backend.scholarkart.in/api/submodules?moduleId=${module._id}`);
    setComponents(res.data);
    setSelectedComponent(null);
    setShowModules(false);
  };

  const handleComponentClick = async (comp) => {
    setSelectedComponent(comp);
    const contentUrls = comp?.contentUrls || (comp?.contentUrl ? [comp.contentUrl] : []);

    const htmlUrl = contentUrls.find(url => url.endsWith(".html") || url.endsWith(".htm"));
    if (htmlUrl) {
      const res = await fetch(htmlUrl);
      const html = await res.text();
      setContentHTML(html);
    }


    

    if (user?._id) {
      await axios.post(`https://backend.scholarkart.in/api/users/${user._id}/complete-submodule`, {
        moduleId: selectedModule._id,
        submoduleId: comp._id
      });
      setCompleted(prev => [...prev, comp._id]);
    }
  };

  const saveForOffline = async (urls, id) => {
    if (!Array.isArray(urls)) urls = [urls];
    const allText = await Promise.all(urls.map(async url => {
      const res = await fetch(url);
      return await res.text();
    }));
    localStorage.setItem(`offline_${id}`, JSON.stringify(allText));
    alert("Saved for offline reading!");
  };

function FlashcardList({ cards }) {
  return (
    <div className="space-y-4 p-2">
      <h3 className="font-semibold text-lg text-gray-800 dark:text-white">🧠 Flashcards</h3>
      {cards.map((card, idx) => (
        <div
          key={idx}
          className="border border-gray-300 dark:border-gray-600 rounded-lg p-3 bg-gray-50 dark:bg-gray-900 shadow"
        >
          <p className="font-medium text-blue-800 dark:text-blue-300">
            Q{idx + 1}: {card.question}
          </p>
          <p className="text-gray-800 dark:text-gray-100 mt-1">
            <strong>Answer:</strong> {card.answer}
          </p>
        </div>
      ))}
    </div>
  );
}

function renderMindMapNode(node, depth = 0) {
  return (
    <div key={node.title} className="ml-4 border-l pl-4 border-gray-300 dark:border-gray-600">
      <div className={`font-semibold ${depth === 0 ? 'text-lg mt-2' : 'text-sm'}`}>
        {node.title}
      </div>
      {Array.isArray(node.children) && node.children.length > 0 && (
        <div className="mt-1 space-y-2">
          {node.children.map((child, i) => renderMindMapNode(child, depth + 1))}
        </div>
      )}
    </div>
  );
}

function getMindMapIcon(title = '') {
  const t = title.toLowerCase();
  if (t.includes('war') || t.includes('battle')) return '⚔️';
  if (t.includes('trade') || t.includes('commerce')) return '📦';
  if (t.includes('policy') || t.includes('law')) return '📜';
  if (t.includes('governor') || t.includes('leader') || t.includes('king')) return '👑';
  if (t.includes('company') || t.includes('eic')) return '🏢';
  if (t.includes('explorer') || t.includes('route')) return '🧭';
  if (t.includes('treaty') || t.includes('agreement')) return '📃';
  return '🟢'; // Default dot
}

function renderMindMapNode(node, depth = 0) {
  const icon = getMindMapIcon(node.title);

  return (
    <div key={node.title} className="ml-4 border-l pl-4 border-gray-300 dark:border-gray-600">
      <div className={`flex items-start gap-2 ${depth === 0 ? 'text-lg font-bold' : 'text-sm font-medium'}`}>
        <span>{icon}</span>
        <span>{node.title}</span>
      </div>
      {Array.isArray(node.children) && node.children.length > 0 && (
        <div className="mt-1 space-y-2">
          {node.children.map((child, i) => renderMindMapNode(child, depth + 1))}
        </div>
      )}
    </div>
  );
}


function QuizRenderer({ questions }) {
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selected, setSelected] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [finished, setFinished] = useState(false);
  const [shuffledQuestions, setShuffledQuestions] = useState([]);

  const shuffleArray = (arr) => arr.sort(() => Math.random() - 0.5);

  useEffect(() => {
    const shuffled = shuffleArray(questions.map(q => ({
      ...q,
      options: shuffleArray([...q.options]),
    })));
    setShuffledQuestions(shuffled);
  }, [questions]);

  // ✅ Protect access to currentQ if not ready
  if (!shuffledQuestions.length) {
    return <div className="text-center p-4">Loading quiz...</div>;
  }

  const currentQ = shuffledQuestions[currentQIndex];

  const handleOptionClick = (opt) => {
    if (!showAnswer) {
      setSelected(opt);
      setShowAnswer(true);
      if (opt === currentQ.answer) {
        setScore(prev => prev + 1);
      }
    }
  };

  const handleNext = () => {
    if (currentQIndex + 1 < shuffledQuestions.length) {
      setCurrentQIndex(prev => prev + 1);
      setSelected(null);
      setShowAnswer(false);
    } else {
      setFinished(true);
    }
  };

  const handleRetry = () => {
    setCurrentQIndex(0);
    setScore(0);
    setSelected(null);
    setShowAnswer(false);
    setFinished(false);
    const reshuffled = shuffleArray(questions.map(q => ({
      ...q,
      options: shuffleArray([...q.options]),
    })));
    setShuffledQuestions(reshuffled);
  };

  if (finished) {
    return (
      <div className="p-6 text-center bg-green-100 dark:bg-green-900 rounded-lg shadow">
        <h3 className="text-2xl font-bold text-green-800 dark:text-green-100 mb-2">🎉 Quiz Completed!</h3>
        <p className="text-lg text-green-700 dark:text-green-300 mb-4">
          You scored <strong>{score}</strong> out of <strong>{shuffledQuestions.length}</strong>
        </p>
        <button
          onClick={handleRetry}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          🔁 Retry Quiz
        </button>
      </div>
    );
  }

  return (
    <div className="bg-green-50 dark:bg-gray-800 p-6 rounded-lg border border-green-200 dark:border-green-700 shadow">
      {/* Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
        <div
          className="bg-green-500 h-3 rounded-full transition-all duration-300"
          style={{ width: `${((currentQIndex + 1) / shuffledQuestions.length) * 100}%` }}
        ></div>
      </div>

      <h3 className="text-xl font-bold text-green-900 dark:text-green-100 mb-4">
        🧠 Question {currentQIndex + 1} of {shuffledQuestions.length}
      </h3>

      <p className="font-semibold text-lg mb-4">{currentQ.question}</p>

      <ul className="space-y-3">
        {currentQ.options.map((opt, i) => {
          const isCorrect = showAnswer && opt === currentQ.answer;
          const isWrong = showAnswer && selected === opt && opt !== currentQ.answer;
          const isSelected = selected === opt;

          return (
            <li
              key={i}
              onClick={() => handleOptionClick(opt)}
              className={`px-4 py-2 rounded border cursor-pointer transition
                ${isCorrect ? 'bg-green-100 border-green-500 text-green-900' : ''}
                ${isWrong ? 'bg-red-100 border-red-500 text-red-900' : ''}
                ${!isCorrect && !isWrong && isSelected ? 'border-blue-400 bg-blue-50' : 'border-gray-300'}
              `}
            >
              {opt}
            </li>
          );
        })}
      </ul>

      {showAnswer && (
        <div className="mt-4 space-y-2">
          <p className="text-sm text-green-700 dark:text-green-300">
            ✅ Correct Answer: <strong>{currentQ.answer}</strong>
          </p>
          {currentQ.explanation && (
            <p className="text-sm italic text-blue-700 dark:text-blue-300">
              💡 {currentQ.explanation}
            </p>
          )}
          <button
            onClick={handleNext}
            className="mt-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            {currentQIndex + 1 < shuffledQuestions.length ? 'Next' : 'Finish'}
          </button>
        </div>
      )}
    </div>
  );
}



  const renderContent = () => {
    if (!selectedComponent) return <div className="text-center p-10">Select a Learning Component</div>;

 
    const contentUrls = selectedComponent?.contentUrls || (selectedComponent?.contentUrl ? [selectedComponent.contentUrl] : []);

    const isAllHTML = contentUrls.every(url => url.endsWith(".html") || url.endsWith(".htm"));
    const isAllPDF = contentUrls.every(url => url.endsWith(".pdf"));
    const isAllYouTube = contentUrls.every(url => url.includes("youtube.com/embed/"));
    const isAllDataText = contentUrls.every(url => url.startsWith("data:text/plain"));

    if (isAllHTML) {
      return (
      <div>
        <div className="flex gap-3">
          {!isSpeaking && (
            <button
              onClick={speakText}
              className="px-4 py-2 text-sm font-semibold bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              🔊 Read Aloud
            </button>
          )}

          {isSpeaking && !isPaused && (
            <button
              onClick={pauseSpeaking}
              className="px-4 py-2 text-sm font-semibold bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              ⏸️ Pause
            </button>
          )}

          {isSpeaking && isPaused && (
            <button
              onClick={resumeSpeaking}
              className="px-4 py-2 text-sm font-semibold bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              ▶️ Resume
            </button>
          )}

          {isSpeaking && (
            <button
              onClick={stopSpeaking}
              className="px-4 py-2 text-sm font-semibold bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              🔇 Stop
            </button>
          )}
        </div>
        <div
          ref={contentRef}
          className="prose prose-sm md:prose-lg dark:prose-invert max-w-none list-disc list-outside [&>ul]:list-disc [&>ul]:pl-5"
          dangerouslySetInnerHTML={{ __html: contentHTML }}
        />
      </div>
      );
    } else if (isAllPDF) {
      return contentUrls.map((url, idx) => (
        <iframe
          key={idx}
          src={url}
          className="w-full h-[80vh] mb-6 rounded-lg shadow"
          title={`PDF Viewer ${idx + 1}`}
        />
      ));
    } else if (isAllDataText) {
      const decoded = atob(contentUrls[0].split(',')[1]);
      return (
        <>

          <div className="prose prose-sm md:prose-base dark:prose-invert max-w-none p-4 whitespace-pre-wrap bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow">
            {(() => {
              const words = decoded.split(/(\s+)/); // keep words + spaces
              let charCount = 0;

              return words.map((word, idx) => {
                const start = charCount;
                charCount += word.length;

                const isHighlighted =
                  spokenCharIndex >= start && spokenCharIndex < start + word.length;

                return (
                  <span
                    key={idx}
                    className={isHighlighted ? 'bg-yellow-200 dark:bg-yellow-600 font-bold text-[1.05em]' : ''}
                  >
                    {word}
                  </span>
                );
              });
            })()}
          </div>
        </>
      );
    } else if (contentUrls[0].startsWith('data:application/json;base64,')) {
        try {
          const json = atob(contentUrls[0].split(',')[1]);
          const data = JSON.parse(json);

          if (Array.isArray(data)) {
            // Legacy flat flashcards
            return <FlashcardList cards={data} />;
          }

          if (data.title && data.children) {
            return (
              <div className="p-4 rounded-lg bg-blue-50 dark:bg-gray-800 border border-blue-200 dark:border-gray-700 shadow">
                <h3 className="text-xl font-bold mb-4 text-blue-900 dark:text-blue-100">🧠 Mind Map</h3>
                {renderMindMapNode(data)}
              </div>
            );
          }

          if (data.type === 'quiz' && Array.isArray(data.questions)) {
            return <QuizRenderer questions={data.questions} />;
          }




          if (data.type === 'takeaways' && typeof data.content === 'string') {
            const markdown = data.content;
            const lines = removeMd(markdown)
            .split(/\n+/)
            .map(line => line.trim())
            .filter(line => line && !/^#+\s*Key Takeaways/i.test(line));

          const grouped = [];
          let currentGroup = { heading: '', items: [] };

          for (const line of lines) {
            if (/^#{2,3}\s*/.test(line)) {
              // New subheading
              if (currentGroup.items.length > 0) {
                grouped.push(currentGroup);
              }
              currentGroup = {
                heading: line.replace(/^#{2,3}\s*/, '').trim(),
                items: []
              };
            } else {
              currentGroup.items.push(line.replace(/^\d+\.\s*/, '')); // remove number
            }
          }
          if (currentGroup.items.length > 0) {
            grouped.push(currentGroup);
          }

          return (
            <div className="space-y-6 bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border dark:border-yellow-600 shadow">
              <h3 className="font-bold text-lg text-yellow-800 dark:text-yellow-100">🔑 Key Takeaways</h3>
              {grouped.map((group, i) => (
                <div key={i} className="space-y-2">
                  <SpeakControls text={lines.join('\n')} />
                  {group.heading && (
                    <h4 className="text-md font-semibold text-yellow-900 dark:text-yellow-200">
                      {group.heading}
                    </h4>
                  )}
                  <ol className="list-decimal list-inside text-base text-black-900 dark:text-black-100 space-y-4">
                    {group.items.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          );
          }

          if (data.sections) {
            const markdown = data.content || '';
            <SpeakControls text={markdown} />
            return (
              <div className="space-y-6">

                <h3 className="text-xl font-semibold">{data.title || '📘 Chapter Notes'}</h3>

                <ReactMarkdown
                  children={markdown}
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    h1: ({ node, ...props }) => (
                      <h1 className="text-2xl font-bold mt-6 mb-2" {...props} />
                    ),
                    h2: ({ node, ...props }) => (
                      <h2 className="text-xl font-semibold mt-5 mb-2" {...props} />
                    ),
                    h3: ({ node, ...props }) => (
                      <h3 className="text-lg font-medium mt-4 mb-2" {...props} />
                    ),
                    p: ({ node, ...props }) => (
                      <p className="my-3 leading-relaxed" {...props} />
                    ),
                    li: ({ node, ...props }) => (
                      <li className="mb-1" {...props} />
                    ),
                  }}
                />
              </div>
            );
          } else if (data.type === 'notes' && typeof data.content === 'string') {
              const sections = data.content.split(/(?=^#{1,3} )/m); // Split at headings (e.g. #, ##, ###)

                const markdown = data.content;
                const plainText = removeMd(markdown);

              return (
                <div className="space-y-6 p-4 bg-white dark:bg-gray-800 rounded shadow border dark:border-gray-700">
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-white">📘 Chapter Notes</h3>

                  <SpeakControls text={plainText} />

                  {sections.map((sec, idx) => (
                    <div key={idx} className="space-y-3">
                      <ReactMarkdown
                        children={sec.trim()}
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                        components={{
                          h1: ({ node, ...props }) => <h1 className="text-2xl font-bold mt-6 mb-2" {...props} />,
                          h2: ({ node, ...props }) => <h2 className="text-xl font-semibold mt-5 mb-2" {...props} />,
                          h3: ({ node, ...props }) => <h3 className="text-lg font-medium mt-4 mb-2" {...props} />,
                          p: ({ node, ...props }) => <p className="my-3 leading-relaxed" {...props} />,
                          li: ({ node, ...props }) => <li className="mb-1" {...props} />,
                        }}
                      />
                    </div>
                  ))}
                </div>
              );
            }


          if (data[0]?.question && data[0]?.answer) {
            return <FlashcardList cards={data} />;
          }

          return <p className="text-red-500">⚠️ Unknown content format</p>;
        } catch (e) {
          return <p className="text-red-500">⚠️ Error loading content</p>;
        }
      } else if (isAllYouTube) {
      return (
        <div className="space-y-6">
          {contentUrls.map((url, idx) => (
            <div key={idx} className="relative w-full" style={{ paddingBottom: '56.25%', height: 0 }}>
              <iframe
                src={url}
                className="absolute top-0 left-0 w-full h-full rounded-lg shadow"
                title={`YouTube Video ${idx + 1}`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ))}
        </div>
      );
    } else {
      return <div className="p-10">Unsupported file type.</div>;
    }
  };

  const startPayment = async () => {
    try {
      const res = await axios.post('https://backend.scholarkart.in/api/payment/order', {
        subjectId,
        userId: user._id
      });
  
      const options = {
        key: "rzp_live_kKWBg4Hh617Za6", // Replace with real key
        amount: res.data.amount,
        currency: "INR",
        name: "eBrary.guru",
        description: "Access to eLearning Module",
        order_id: res.data.id,
        handler: async (response) => {
          // Save to DB after payment success
          await axios.post(`https://backend.scholarkart.in/api/payment/success`, {
            userId: user._id,
            subjectId,
            paymentInfo: response
          });
          alert("🎉 Payment successful! Access granted.");
          setHasAccess(true);
        },
        prefill: {
          name: user.displayname,
          email: user.email
        },
        theme: {
          color: "#ff7a00"
        }
      };
  
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error("Payment initiation failed", error);
      alert("Payment failed. Please try again.");
    }
  };

  const contentUrls = selectedComponent?.contentUrls || (selectedComponent?.contentUrl ? [selectedComponent.contentUrl] : []);
//  if (hasAccess) {    
  if (!hasAccess) {    
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-900 text-center px-4">
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow max-w-lg">
          <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-4">🔒 Premium Access Required</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            This eLearning module is a premium feature. You need to purchase access to view this content.
            <br />
            <span className="text-lg font-bold text-orange-600">₹{subjectPrice} one-time</span>
          </p>
          <button
            onClick={startPayment}
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2 rounded"
          >
            💳 Buy Access for ₹{subjectPrice}
          </button>
        </div>
      </div>
    );
  }

  
  return (
    <div className="flex flex-col md:flex-row h-screen overflow-hidden bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {/* Header */}
      <header className="flex items-center justify-between px-4 py-3 border-b border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 md:hidden">
        <button onClick={() => setShowModules(!showModules)} className="text-gray-700 dark:text-white">
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex gap-4 items-center">
          <h1 className="text-sm font-medium text-gray-600 dark:text-gray-300">Curriculum Viewer</h1>
          <button onClick={() => setIsDarkMode(prev => !prev)} className="text-xs px-3 py-1 bg-gray-300 dark:bg-gray-700 rounded-full">
            {isDarkMode ? '🌙' : '☀️'}
          </button>
          
        </div>
      </header>

      {/* Module Panel */}
      {(showModules || window.innerWidth >= 768) && (
        <div className="w-full md:w-1/4 bg-[#f7f7f7] dark:bg-[#1e293b] border-r border-gray-300 dark:border-gray-700 overflow-y-auto p-4 md:pt-8">

          <h2 className="text-lg font-bold mb-4 text-[#033D7A] dark:text-orange-400">{subjectName || "Subject"}</h2>
          
          {modules.map((mod) => (
            <div key={mod._id} className="mb-4">
              <button
                onClick={() => handleModuleClick(mod)}
                className={`block w-full text-left p-2 rounded-lg transition-all text-sm font-semibold shadow-sm
                  ${selectedModule?._id === mod._id
                    ? "bg-[#ff7a00] text-white"
                    : "bg-white dark:bg-gray-800 text-gray-800 dark:text-white border border-gray-300 dark:border-gray-600 hover:bg-orange-100 dark:hover:bg-orange-800"}`}
              >
                {mod.moduleName}
              </button>
              {selectedModule?._id === mod._id && components.length > 0 && (
                <div className="mt-2 pl-3">
                  {components.map((comp) => (
                    <div key={comp._id} className="flex items-center justify-between">
                      <button
                        onClick={() => handleComponentClick(comp)}
                        className="block w-full text-left text-sm py-1 px-2 hover:text-[#ff7a00] text-gray-700 dark:text-gray-300"
                      >
                        ▸ {comp.submoduleType.replace(/([A-Z])/g, ' $1')}
                      </button>
                      {completed.includes(comp._id) && <CheckCircle className="w-4 h-4 text-green-500" />}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto p-4 md:p-8 bg-white dark:bg-gray-900 transition-colors duration-300">
          <ChatWidget subjectId={subjectId} />
          
          <button onClick={() => setIsDarkMode(prev => !prev)} className="text-xs px-3 py-1 bg-gray-300 dark:bg-gray-700 rounded-full">
            {isDarkMode ? '🌙' : '☀️'}
          </button>
        {selectedModule && (
          <div className="mb-6">
            <h2 className="text-xl font-bold text-[#055862] dark:text-orange-400">
              {selectedModule.moduleName}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 text-sm">{selectedModule.moduleDescription}</p>
          </div>
        )}
        {selectedComponent && selectedComponent.submoduleType !== "Quiz" && (
          <div className="mb-4 flex justify-between items-center">
            <button
              onClick={() => saveForOffline(contentUrls, selectedComponent._id)}
              className="text-sm text-white bg-[#055862] px-4 py-1 rounded hover:bg-teal-700"
            >
              Save for Offline
            </button>
          </div>
        )}
        {renderContent()}
      </main>
    </div>
  );
}